import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Download,
  Lock,
  LogOut,
  RefreshCcw,
  Sparkles,
  Upload,
  Zap,
} from "lucide-react";

import { useAccountStatus } from "../hooks/useAccountStatus";
import {
  collectLocalAppState,
  getLocalAppStateSummary,
  restoreLocalAppState,
  summarizeAppState,
} from "../lib/accountStorage";
import { hasSupabaseConfig, supabase } from "../lib/supabaseClient";

const LOGIN_PENDING_KEY = "gwrene_account_login_pending";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.56 2.7-3.87 2.7-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.81.54-1.85.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.98v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.98A9 9 0 0 0 0 9c0 1.45.35 2.83.98 4.03l2.97-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .98 4.97l2.97 2.33C4.66 5.17 6.65 3.58 9 3.58Z" />
    </svg>
  );
}

// Newsletter opt-in goes to Kit (ConvertKit), the same list the homepage form uses.
// This is fire-and-forget and never blocks or affects the Supabase magic-link login.
const KIT_FORM_ID = "9386699";
const KIT_ACCOUNT_FRAME = "kit-account-frame";

function subscribeToKit(email) {
  try {
    const trimmed = (email || "").trim();
    if (!trimmed) return;
    const form = document.createElement("form");
    form.action = `https://app.convertkit.com/forms/${KIT_FORM_ID}/subscriptions`;
    form.method = "post";
    form.target = KIT_ACCOUNT_FRAME;
    form.style.display = "none";
    const field = document.createElement("input");
    field.type = "email";
    field.name = "email_address";
    field.value = trimmed;
    form.appendChild(field);
    document.body.appendChild(form);
    form.submit();
    window.setTimeout(() => form.remove(), 0);
  } catch {
    // Never let a newsletter failure affect sign-in.
  }
}

function formatPeriodEnd(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatSavedTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function describeSummary(summary) {
  return `${summary.completedLessons} lessons complete · ${summary.savedCount} saved items · ${summary.practiceDays} practice days`;
}

function friendlyAccountMessage(value) {
  const message = typeof value === "string" ? value : value?.message || "";
  if (!message) return "";
  const lower = message.toLowerCase();

  if (
    lower.includes("payment finished") ||
    lower.includes("checkout finished") ||
    lower.includes("checkout was cancelled") ||
    lower.includes("signed out") ||
    lower.includes("backed up") ||
    lower.includes("restored")
  ) {
    return message;
  }

  if (lower.includes("permission denied")) {
    return "I could not read your account data yet. Refresh once, or check the Supabase table permissions.";
  }
  if (lower.includes("failed to fetch") || lower.includes("network")) {
    return "I could not reach the account server. Check your connection and try again.";
  }
  if (lower.includes("environment variables")) {
    return "This feature is not configured on the live server yet.";
  }
  if (
    (lower.includes("stripe") || lower.includes("checkout") || lower.includes("billing")) &&
    (
      lower.includes("could not") ||
      lower.includes("failed") ||
      lower.includes("missing") ||
      lower.includes("not configured") ||
      lower.includes("not open") ||
      lower.includes("requires") ||
      lower.includes("error")
    )
  ) {
    return "Checkout is not ready yet. Finish the Stripe setup first.";
  }

  return message;
}

// Stripe's raw status words ("refunded", "incomplete") are database language,
// so say what each one means for the person reading the page.
const PLAN_STATUS_LABELS = {
  lifetime: "Pro active",
  active: "Pro active",
  trialing: "Pro active",
  refunded: "Refunded. Pro access ended.",
  incomplete: "Payment not finished. Pro is not unlocked.",
  incomplete_expired: "Payment not finished. Pro is not unlocked.",
  past_due: "Payment failed. Pro is paused.",
  unpaid: "Payment failed. Pro is paused.",
  canceled: "Cancelled. Pro access ended.",
  inactive: "Free plan",
};

function getPlanLabel(account) {
  if (account.isPro) return "Pro active";
  const status = account.subscription?.subscription_status;
  if (status) return PLAN_STATUS_LABELS[status] || "Free plan";
  return "Free plan";
}

function getBillingModeCopy(billingConfig, account) {
  if (account?.isPro) {
    return {
      title: "Your Pro access is active.",
      body: "Pro is unlocked on this account for good. Receipts and billing shows your payment receipt.",
    };
  }

  if (billingConfig.liveConfigured) {
    return {
      title: "Ready to upgrade.",
      body: "Pay once, securely with Stripe. Pro unlocks automatically for this email after payment. No subscription.",
    };
  }

  if (billingConfig.stripeMode === "test") {
    return {
      title: "Checkout is connected in test mode.",
      body: "Use this for sandbox testing. Switch Vercel to live Stripe keys after Stripe finishes account verification.",
    };
  }

  if (billingConfig.stripeMode === "live") {
    return {
      title: "Live keys are present, but live checkout is not fully armed yet.",
      body: "Check the live price, webhook secret, and STRIPE_REQUIRE_LIVE setting before sending people to checkout.",
    };
  }

  return {
    title: "Checkout setup needs one more pass.",
    body: "Add the missing server variables, redeploy, then refresh this page.",
  };
}

export default function AccountPage() {
  const account = useAccountStatus();
  const [email, setEmail] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [message, setMessage] = useState("");
  const [checkoutNotice, setCheckoutNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [remoteBackup, setRemoteBackup] = useState({
    loading: false,
    updatedAt: "",
    exportedAt: "",
    summary: null,
  });
  const [billingConfig, setBillingConfig] = useState({
    loading: true,
    checkoutConfigured: false,
    portalConfigured: false,
    webhookConfigured: false,
    liveConfigured: false,
    requireLive: false,
    stripeMode: "not configured",
    missing: [],
  });
  const summary = getLocalAppStateSummary();
  const refreshAccountStatus = account.refresh;

  useEffect(() => {
    document.title = "Account | Guitar With Rene";
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadBillingConfig() {
      try {
        const response = await fetch("/api/billing-config");
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        setBillingConfig({
          loading: false,
          checkoutConfigured: Boolean(response.ok && data.checkoutConfigured),
          portalConfigured: Boolean(response.ok && data.portalConfigured),
          webhookConfigured: Boolean(response.ok && data.webhookConfigured),
          liveConfigured: Boolean(response.ok && data.liveConfigured),
          requireLive: Boolean(response.ok && data.requireLive),
          stripeMode: response.ok && data.stripeMode ? data.stripeMode : "not configured",
          missing: response.ok && Array.isArray(data.missing) ? data.missing : [],
          priceLabel: response.ok && data.priceLabel ? data.priceLabel : "€29",
          earlyOffer: response.ok && data.earlyOffer ? data.earlyOffer : null,
        });
      } catch {
        if (!cancelled) {
          setBillingConfig({
            loading: false,
            checkoutConfigured: false,
            portalConfigured: false,
            webhookConfigured: false,
            liveConfigured: false,
            requireLive: false,
            stripeMode: "not configured",
            missing: [],
          });
        }
      }
    }

    loadBillingConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const checkoutMessageTimer = window.setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const checkoutStatus = params.get("checkout");

      if (checkoutStatus === "success") {
        setCheckoutNotice("success");
        setMessage("");
        refreshAccountStatus();
        window.history.replaceState(null, "", "/account");
      }
      if (checkoutStatus === "cancelled") {
        setCheckoutNotice("cancelled");
        setMessage("Checkout was cancelled. No payment was made.");
        window.history.replaceState(null, "", "/account");
      }
    }, 0);

    return () => window.clearTimeout(checkoutMessageTimer);
  }, [refreshAccountStatus]);

  const loadRemoteBackup = useCallback(async () => {
    if (!supabase || !account.user) {
      setRemoteBackup({ loading: false, updatedAt: "", exportedAt: "", summary: null });
      return;
    }

    setRemoteBackup((current) => ({ ...current, loading: true }));
    const { data, error } = await supabase
      .from("user_app_state")
      .select("state, updated_at")
      .eq("user_id", account.user.id)
      .maybeSingle();

    if (error) {
      setRemoteBackup({ loading: false, updatedAt: "", exportedAt: "", summary: null });
      setMessage(friendlyAccountMessage(error));
      return;
    }

    setRemoteBackup({
      loading: false,
      updatedAt: data?.updated_at || "",
      exportedAt: data?.state?.exportedAt || "",
      summary: data?.state ? summarizeAppState(data.state) : null,
    });
  }, [account.user]);

  useEffect(() => {
    const backupTimer = window.setTimeout(() => {
      loadRemoteBackup();
    }, 0);
    return () => window.clearTimeout(backupTimer);
  }, [loadRemoteBackup]);

  async function sendMagicLink(event) {
    event.preventDefault();
    if (!supabase || !email.trim()) return;
    setBusy("login");
    setMessage("");
    sessionStorage.setItem(LOGIN_PENDING_KEY, "1");
    // Fire-and-forget newsletter opt-in. Runs beside auth, never blocks it.
    if (newsletterOptIn) {
      subscribeToKit(email);
    }
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}/member`,
      },
    });
    setBusy("");
    setMessage(error ? friendlyAccountMessage(error) : "Check your email. The link will bring you back to the members area.");
  }

  async function signInWithGoogle() {
    if (!supabase) return;
    setBusy("google");
    setMessage("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/member`,
      },
    });
    // On success the browser redirects to Google immediately, so there is
    // nothing to reset. Only clear busy/show a message if it failed to start.
    if (error) {
      setBusy("");
      setMessage(friendlyAccountMessage(error));
    }
  }

  async function signOut() {
    if (!supabase) return;
    setBusy("signout");
    await supabase.auth.signOut();
    setBusy("");
    setMessage("Signed out.");
  }

  async function backupProgress() {
    if (!supabase || !account.user) return;
    setBusy("backup");
    setMessage("");
    const appState = collectLocalAppState();
    const { data, error } = await supabase
      .from("user_app_state")
      .upsert({
        user_id: account.user.id,
        state: appState,
        updated_at: new Date().toISOString(),
      })
      .select("state, updated_at")
      .maybeSingle();
    setBusy("");
    if (error) {
      setMessage(friendlyAccountMessage(error));
      return;
    }
    setRemoteBackup({
      loading: false,
      updatedAt: data?.updated_at || new Date().toISOString(),
      exportedAt: data?.state?.exportedAt || appState.exportedAt,
      summary: data?.state ? summarizeAppState(data.state) : summarizeAppState(appState),
    });
    setMessage("This browser is backed up to your account.");
  }

  async function restoreProgress() {
    if (!supabase || !account.user) return;
    setBusy("restore");
    setMessage("");
    const { data, error } = await supabase
      .from("user_app_state")
      .select("state, updated_at")
      .eq("user_id", account.user.id)
      .maybeSingle();

    if (error) {
      setMessage(friendlyAccountMessage(error));
    } else if (!data?.state) {
      setMessage("There is no account backup yet. Back up this browser first.");
    } else {
      restoreLocalAppState(data.state);
      setRemoteBackup({
        loading: false,
        updatedAt: data.updated_at || "",
        exportedAt: data.state.exportedAt || "",
        summary: summarizeAppState(data.state),
      });
      setMessage("Progress restored. Refresh the members area to see the synced state.");
    }
    setBusy("");
  }

  async function goToCheckout() {
    if (!account.session) return;
    setBusy("checkout");
    setMessage("");
    const response = await fetch("/api/create-checkout-session", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${account.session.access_token}`,
      },
    });
    const data = await response.json().catch(() => ({}));
    setBusy("");
    if (!response.ok || !data.url) {
      setMessage(friendlyAccountMessage(data.error || "Could not start checkout yet."));
      return;
    }
    window.location.href = data.url;
  }

  async function openBillingPortal() {
    if (!account.session) return;
    setBusy("portal");
    setMessage("");
    const response = await fetch("/api/create-billing-portal-session", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${account.session.access_token}`,
      },
    });
    const data = await response.json().catch(() => ({}));
    setBusy("");
    if (!response.ok || !data.url) {
      setMessage(friendlyAccountMessage(data.error || "Could not open billing yet."));
      return;
    }
    window.location.href = data.url;
  }

  const remoteSavedAt = formatSavedTime(remoteBackup.exportedAt || remoteBackup.updatedAt);
  const remoteBackupLine = remoteBackup.loading
    ? "Checking account backup..."
    : remoteBackup.summary
      ? `${describeSummary(remoteBackup.summary)}${remoteSavedAt ? ` · saved ${remoteSavedAt}` : ""}`
      : "No account backup yet.";
  const accountMessage = friendlyAccountMessage(message || account.error);
  const magicLinkSent = Boolean(
    !account.user &&
      accountMessage.toLowerCase().includes("check your email") &&
      accountMessage.toLowerCase().includes("members area")
  );
  const localMatchesBackup = Boolean(
    remoteBackup.summary &&
      summary.completedLessons === remoteBackup.summary.completedLessons &&
      summary.savedCount === remoteBackup.summary.savedCount &&
      summary.practiceDays === remoteBackup.summary.practiceDays,
  );

  return (
    <div className="page-shell light-page account-page">
      <div className="lg-ambient" aria-hidden="true">
        <span className="lg-blob lg-blob-1" />
        <span className="lg-blob lg-blob-2" />
        <span className="lg-blob lg-blob-3" />
      </div>

      <main className="site-container account-container">
        <a
          href="/member"
          className="account-back-link"
        >
          <ArrowLeft size={15} strokeWidth={2.4} /> Back to your practice
        </a>
        <section className="account-hero">
          <div className="account-hero-copy">
            {!account.user && (
              <div className="section-badge">
                <Sparkles size={17} strokeWidth={2.25} />
                <span>Free account</span>
              </div>
            )}
            <h1>{account.user ? "Your account" : "Save your guitar progress"}</h1>
            {!account.user && (
              <>
                <p className="account-save-summary">
                  Sign in once. Your lessons, streak, saved chords, and Pro access stay tied to your email.
                </p>
                <div className="account-value-list" aria-label="What your account saves">
                  <span><CheckCircle2 size={16} strokeWidth={2.5} /> Lesson progress</span>
                  <span><CheckCircle2 size={16} strokeWidth={2.5} /> XP and streak</span>
                  <span><CheckCircle2 size={16} strokeWidth={2.5} /> Pro access</span>
                </div>
              </>
            )}
          </div>

          {account.user ? (
            <div className={`account-status-card${account.isPro ? " is-pro" : ""}`}>
              <span>Signed in</span>
              <strong>{account.isPro ? "Pro active" : "Free account"}</strong>
              <p>
                {account.subscription?.subscription_status === "lifetime"
                  ? "Pro owned. No renewal."
                  : account.subscription?.current_period_end
                    ? `Renews or ends ${formatPeriodEnd(account.subscription.current_period_end)}`
                    : account.user?.email || ""}
              </p>
            </div>
          ) : (
            <div className="account-status-card account-login-card">
              <iframe
                name={KIT_ACCOUNT_FRAME}
                title="Newsletter signup"
                aria-hidden="true"
                tabIndex={-1}
                style={{ display: "none" }}
              />
              {magicLinkSent ? (
                // In-place swap instead of appending below the form: the confirmation
                // replaces the form in the same card slot so it's visible without
                // scrolling, rather than growing the card taller and pushing the
                // message below the fold (2026-07-11 fix).
                <div className="account-email-sent" role="status" aria-live="polite">
                  <span className="account-email-sent-icon" aria-hidden="true">
                    <CheckCircle2 size={20} strokeWidth={2.6} />
                  </span>
                  <div>
                    <strong>Check your email now</strong>
                    <p>
                      We sent a secure sign-in link to <span>{email.trim()}</span>.
                      Open it on this device and you will come straight back to your practice area.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <strong>Save your progress for free</strong>
                  <p>
                    New here or coming back? Use Google or an email link. No password to remember.
                  </p>
                  <button
                    type="button"
                    className="account-google-btn"
                    onClick={signInWithGoogle}
                    disabled={!hasSupabaseConfig || Boolean(busy)}
                  >
                    <GoogleIcon />
                    {busy === "google" ? "Connecting..." : "Continue with Google"}
                  </button>
                  <div className="account-divider" role="separator" aria-label="or use email">
                    <span>or use email</span>
                  </div>
                  <form
                    onSubmit={sendMagicLink}
                    className="account-magic-form"
                  >
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      aria-label="Email"
                      autoComplete="email"
                      name="email"
                      required
                      disabled={!hasSupabaseConfig || Boolean(busy)}
                    />
                    <label
                      className="account-newsletter-optin"
                    >
                      <input
                        type="checkbox"
                        checked={newsletterOptIn}
                        onChange={(event) => setNewsletterOptIn(event.target.checked)}
                        disabled={busy === "login"}
                      />
                      Email me new lessons and offers.
                    </label>
                    <button
                      type="submit"
                      disabled={!hasSupabaseConfig || Boolean(busy)}
                    >
                      <Zap size={16} strokeWidth={2.45} />
                      {busy === "login" ? "Sending..." : "Email sign-in link"}
                    </button>
                  </form>
                </>
              )}
              <p className="account-login-trust">
                <Lock size={12} strokeWidth={2.4} />
                No password. No card. No spam.
              </p>
            </div>
          )}
        </section>

        {checkoutNotice === "success" && account.user && (
          <section className={`account-checkout-success${account.isPro ? " is-active" : ""}`}>
            <div className="account-checkout-success-icon" aria-hidden="true">
              <CheckCircle2 size={24} strokeWidth={2.55} />
            </div>
            <div>
              <span>Payment complete</span>
              <h2>{account.isPro ? "You are in. Pro is active." : "Payment received. Unlocking Pro now."}</h2>
              <p>
                {account.isPro
                  ? "Your Pro lessons, paths, and member tools are now unlocked for this email."
                  : "If this still shows as free after a few seconds, refresh once so the Stripe webhook can finish syncing."}
              </p>
            </div>
            <a href="/pro">
              Open Pro
              <ArrowRight size={15} strokeWidth={2.45} />
            </a>
          </section>
        )}

        {!hasSupabaseConfig && (
          <section className="account-panel account-warning-panel">
            <Lock size={22} strokeWidth={2.35} />
            <div>
              <h2>Local Supabase connection is missing</h2>
              <p>
                Add `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY` to `.env.local`,
                then restart the dev server. Vercel production variables do not load
                automatically on localhost.
              </p>
            </div>
          </section>
        )}

        {account.user && (
          <>
            <section className="account-grid">
              <article className="account-panel">
                <div className="account-panel-head">
                  <CheckCircle2 size={20} strokeWidth={2.35} />
                  <div>
                    <h2>Manage account</h2>
                    <p>Sign out or refresh your status.</p>
                  </div>
                </div>
                <div className="account-actions">
                  <button type="button" onClick={signOut} disabled={busy === "signout"}>
                    <LogOut size={15} strokeWidth={2.45} />
                    Sign out
                  </button>
                  <button type="button" onClick={account.refresh} disabled={account.loading}>
                    <RefreshCcw size={15} strokeWidth={2.45} />
                    Refresh status
                  </button>
                </div>
              </article>

              <article className="account-panel">
                <div className="account-panel-head">
                  <Upload size={20} strokeWidth={2.35} />
                  <div>
                    <h2>Autosave progress</h2>
                    <p>While signed in, this browser backs itself up after changes.</p>
                  </div>
                </div>
                <div className="account-sync-details">
                  {localMatchesBackup ? (
                    <div>
                      <span>In sync</span>
                      <strong>{remoteBackupLine}</strong>
                    </div>
                  ) : (
                    <>
                      <div>
                        <span>This browser</span>
                        <strong>{describeSummary(summary)}</strong>
                      </div>
                      <div>
                        <span>Account backup</span>
                        <strong>{remoteBackupLine}</strong>
                      </div>
                    </>
                  )}
                </div>
                <div className="account-actions">
                  <button type="button" onClick={backupProgress} disabled={busy === "backup"}>
                    <Upload size={15} strokeWidth={2.45} />
                    {busy === "backup" ? "Backing up..." : "Back up now"}
                  </button>
                  <button type="button" onClick={restoreProgress} disabled={busy === "restore" || !remoteBackup.summary}>
                    <Download size={15} strokeWidth={2.45} />
                    {busy === "restore" ? "Restoring..." : "Restore saved progress"}
                  </button>
                </div>
              </article>
            </section>
          </>
        )}

        {account.user && (
        <section className="account-panel account-subscription-panel">
          <div className="account-panel-head">
            <Lock size={20} strokeWidth={2.35} />
            <div>
              <h2>Pro access</h2>
              <p>
                Buy Pro, check your access, and find your receipt for this email.
              </p>
            </div>
          </div>
          {!account.isPro && (
            <div className="account-subscription-state">
              <span>Status</span>
              <strong>{getPlanLabel(account)}</strong>
            </div>
          )}
          {billingConfig.loading ? (
            <p className="account-checkout-note">Checking checkout setup...</p>
          ) : !billingConfig.checkoutConfigured ? (
            <div className="account-checkout-note">
              <strong>Checkout is not connected yet.</strong>
              <span>
                Add the missing server variables, deploy again, then refresh this page.
                {billingConfig.missing.length ? ` Missing: ${billingConfig.missing.join(", ")}.` : ""}
              </span>
            </div>
          ) : (
            <>
              <div className={`account-checkout-note${billingConfig.liveConfigured ? " is-live" : ""}`}>
                <strong>{getBillingModeCopy(billingConfig, account).title}</strong>
                <span>
                  {getBillingModeCopy(billingConfig, account).body}
                  {billingConfig.missing.length ? ` Missing: ${billingConfig.missing.join(", ")}.` : ""}
                </span>
              </div>
              {!account.isPro && (
                <div className="account-pro-value-list" aria-label="What Pro unlocks">
                  <span><CheckCircle2 size={14} strokeWidth={2.5} /> 55 more guided days after the free week</span>
                  <span><CheckCircle2 size={14} strokeWidth={2.5} /> Song trainer, Tone Lab, and recording practice</span>
                  <span><CheckCircle2 size={14} strokeWidth={2.5} /> Pay once. 14-day money-back guarantee.</span>
                </div>
              )}
              <div className="account-actions">
                {!account.isPro && (
                  <button type="button" onClick={goToCheckout} disabled={!account.user || account.isPro || busy === "checkout"}>
                    <Zap size={15} strokeWidth={2.45} />
                    {billingConfig.liveConfigured
                      ? `Get Pro for ${billingConfig.earlyOffer?.priceLabel || billingConfig.priceLabel || "€29"}`
                      : "Test Pro checkout"}
                    <ArrowRight size={14} strokeWidth={2.45} />
                  </button>
                )}
                {billingConfig.portalConfigured && account.subscription?.stripe_customer_id && (
                  <button type="button" onClick={openBillingPortal} disabled={busy === "portal"}>
                    <Lock size={15} strokeWidth={2.45} />
                    Receipts and billing
                  </button>
                )}
              </div>
            </>
          )}
        </section>
        )}

        {accountMessage && !magicLinkSent && (
          <p className="account-message">{accountMessage}</p>
        )}
      </main>

    </div>
  );
}
